import { useRootAppStore } from "@hooks";
import React, { useState, useRef, useCallback } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { bottom } from "safe-area-insets";
interface Position {
	x: number;
	y: number;
}

export default observer(function DraggableChatToggleButton() {
	const [position, setPosition] = useState<Position>({
		x: 24,
		y: isCapacitorPlatform() ? 80 + bottom : 80,
	});
	const [dragging, setDragging] = useState<boolean>(false);
	const [startPosition, setStartPosition] = useState<Position | null>(null);
	const [isInTrash, setIsInTrash] = useState<boolean>(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const { chatViewStore } = useRootAppStore();

	const toggleChatWindow = () => {
		chatViewStore.toggleChatBox();
	};

	const containerRef = useRef<HTMLDivElement>(null);
	const trashRef = useRef<HTMLDivElement>(null);

	const handleStart = useCallback((e: React.TouchEvent) => {
		e.preventDefault();
		setStartPosition({
			x: e.touches[0].clientX,
			y: e.touches[0].clientY,
		});
		setDragging(true);
	}, []);

	const handleMove = useCallback(
		(e: React.TouchEvent) => {
			e.preventDefault();
			if (!chatViewStore.chatBoxVisible && dragging && startPosition) {
				const movementX = e.touches[0].clientX - startPosition.x;
				const movementY = startPosition.y - e.touches[0].clientY;

				const screenWidth =
					window.innerWidth - 0.125 * window.innerWidth;
				const screenHeight =
					window.innerHeight - 0.1 * window.innerHeight;

				const newX = Math.max(
					0,
					Math.min(
						position.x + movementX,
						screenWidth - containerRef.current!.offsetWidth
					)
				);
				const newY = Math.max(
					0,
					Math.min(
						position.y + movementY,
						screenHeight - containerRef.current!.offsetHeight
					)
				);

				setPosition({
					x: newX,
					y: newY,
				});

				const drawerHeight = screenHeight - 0.8 * screenHeight;
				const drawerWidth = screenWidth;
				const isMovingUpwards = movementY < 0;

				if (
					trashRef.current &&
					newY < drawerHeight &&
					newX >= 0.25 * drawerWidth &&
					newX <= 0.8 * drawerWidth
				) {
					setIsInTrash(true);
				} else {
					setIsInTrash(false);
				}

				setStartPosition({
					x: e.touches[0].clientX,
					y: e.touches[0].clientY,
				});

				if (isInTrash) {
					setIsDrawerOpen(true);
				} else {
					setIsDrawerOpen(isMovingUpwards ? false : true);
				}
			}
		},
		[dragging, startPosition, position]
	);

	const handleEnd = useCallback(() => {
		setDragging(false);
		setStartPosition(null);

		if (isInTrash) {
			chatViewStore.closeChat();
			setPosition({ x: 0, y: 0 });
		}

		setIsDrawerOpen(false);
		setIsInTrash(false);
	}, [isInTrash]);

	const trashCanPadding =
		isCapacitorPlatform() && isDrawerOpen ? { bottom: bottom } : {};

	if (chatViewStore.chatBoxVisible) {
		return null;
	}

	return (
		<>
			<ErrorBoundaryWithPropagation
				componentName={"Mobile/ChatDraggableToggleButton.tsx"}
			>
				<div
					ref={containerRef}
					className="chat-floating"
					style={{
						// position: "fixed",
						bottom: position.y,
						left: position.x,
						// zIndex: 5,
					}}
				>
					<button
						className={classNames(
							"btn--floating btn btn--round btn--round--base btn--icon",
							{
								"btn--floating--active":
									chatViewStore.chatBoxVisible,
								"btn--floating--trashed": isInTrash,
							}
						)}
						onClick={toggleChatWindow}
						onTouchStart={(e) => handleStart(e)}
						onTouchMove={(e) => handleMove(e)}
						onTouchEnd={() => handleEnd()}
					>
						<i
							className={classNames(
								"u-icon u-icon--big u-color--core--snow",
								{
									"u-icon--chat--active":
										!chatViewStore.chatBoxVisible,
									"u-icon--chat--selected":
										chatViewStore.chatBoxVisible,
								}
							)}
						/>
					</button>
				</div>

				<div
					ref={trashRef}
					className={classNames("chat__trash", {
						active: isInTrash,
						appear: isDrawerOpen,
					})}
					style={trashCanPadding}
				>
					<i className="u-icon u-icon--tny u-icon--close u-color--core--snow" />
				</div>
			</ErrorBoundaryWithPropagation>
		</>
	);
});
